/**
 * SN_COM_LAYOUT — 공용 페이지 (메뉴디자이너 1단계 · 현황). 2026-09-29.
 *
 * 메뉴마다 tsx 를 만들지 않는다. sys_formlayout(영역 배치)을 읽어 조회조건 · 그리드 · 나눔 · 탭을 놓고,
 * 영역 안은 지금처럼 sys_formctrl · sys_formgridcolumn(getInitFormData) 으로 채운다.
 * 조회는 sys_query `{메뉴코드 소문자}_01` 을 fnGetDataSet 으로 — 결과 data[0], data[1] … 이 그리드 순서대로 들어간다.
 *
 * 메뉴 조회(BE MenuService)가 sys_formlayout 에 줄이 있는 메뉴의 nm_file 을 SN_COM_LAYOUT 으로 바꿔 주고,
 * MainLayout 이 모듈과 무관하게 이 파일을 찾는다.
 *
 * 디자이너의 미리보기도 같은 LayoutRuntime 을 쓴다 — 정의는 초안에서, 조회는 미리보기 API 로.
 * 저장은 2단계(등록)에서 붙는다. 지금은 조회만이라 툴바는 onSearch 만 받는다.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import SnForm from "@widgets/SnForm";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTab from "@shared/components/SnTab";
import SnResizeBar from "@shared/components/SnResizeBar";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { menuInfo } from "@shared/type/CommonType";
import { DefRow, fetchDesignLayout } from "@shared/api/designApi";

interface Props {
  Menuprops: menuInfo;
}

/** 배치 줄을 트리로 */
export interface LayoutNode {
  id: string;
  fg: string; // V · H · TAB · SEARCH · GRID · FORM
  size?: string | null;
  title?: string | null;
  children: LayoutNode[];
}

/** ID 비교용 — 앞뒤 공백을 지운다(char 칼럼 · 손으로 넣은 값). 없으면 빈 문자열 */
const idOf = (v: unknown) => (v == null ? "" : String(v).trim());

export function buildLayoutTree(rows: DefRow[]): LayoutNode | null {
  if (!Array.isArray(rows)) return null;
  const byId = new Map<string, LayoutNode>();
  rows.forEach((r) => {
    byId.set(idOf(r.id_target), {
      id: idOf(r.id_target),
      fg: String(r.fg_area || "").trim().toUpperCase(),
      size: r.dc_size ?? null,
      title: r.nm_title ?? null,
      children: [],
    });
  });
  let root: LayoutNode | null = null;
  const sorted = [...rows].sort((a, b) => Number(a.no_seq ?? 0) - Number(b.no_seq ?? 0));
  sorted.forEach((r) => {
    const node = byId.get(idOf(r.id_target))!;
    const parentId = idOf(r.id_layout_parent);
    const parent = parentId ? byId.get(parentId) : undefined;
    if (parent) parent.children.push(node);
    else if (!root) root = node;
  });
  return root;
}

/** 그리드 영역 ID 를 배치 순서(깊이 우선)로 — data[i] 가 이 순서로 들어간다 */
export function gridIdsOf(node: LayoutNode | null, out: string[] = []): string[] {
  if (!node) return out;
  if (node.fg === "GRID") out.push(node.id);
  node.children.forEach((c) => gridIdsOf(c, out));
  return out;
}

export interface LayoutRuntimeProps {
  cdMenu: string;
  layout: DefRow[];
  formData: any;
  formControlData: any[];
  /** 그리드 ID → 칼럼 정의(sys_formgridcolumn 행) */
  grids: Record<string, any[]>;
  /** 조회조건 값 → getDataSet 모양의 결과(data[0], data[1] …) */
  onSearch: (params: Record<string, any>) => Promise<any[][] | undefined>;
  /** 등록 화면(2단계)에서 편집을 켠다. 현황은 false */
  editable?: boolean;
  /**
   * 메뉴디자이너 미리보기에서만 넘긴다 — 조회조건을 끌어 옮기기 · X 삭제 · 크기 조정 (2026-10-02).
   * 받은 쪽(디자이너)이 초안에 반영한다. 실제 메뉴는 넘기지 않으므로 지금과 같다.
   */
  onSearchControlsChange?: (visible: any[], hidden: any[]) => void;
}

export interface LayoutRuntimeRef {
  onSearch: () => Promise<void>;
  /** 조회조건 패널의 지금 값. 디자이너 쿼리 탭 [실행] 이 같은 조건으로 돌리려고 읽는다 */
  getParams: () => Record<string, any>;
  /** 그리드 ID → 칼럼 → 지금 폭(px). 사용자가 끌어 놓은 폭을 디자이너가 저장할 때 읽는다 */
  getColumnWidths: () => Record<string, Record<string, number>>;
}

/** 배치 트리를 그린다. 공용 페이지와 디자이너 미리보기가 함께 쓴다 */
export const LayoutRuntime = forwardRef<LayoutRuntimeRef, LayoutRuntimeProps>(
  ({ cdMenu, layout, formData, formControlData, grids, onSearch, editable = false, onSearchControlsChange }, ref) => {
    const searchRef = useRef<any>(null);
    const gridRefs = useRef<Record<string, React.RefObject<any>>>({});
    const [dataSources, setDataSources] = useState<Record<string, any[]>>({});

    const tree = useMemo(() => buildLayoutTree(layout), [layout]);
    const gridIds = useMemo(() => gridIdsOf(tree), [tree]);

    const gridRef = (id: string) => {
      if (!gridRefs.current[id]) gridRefs.current[id] = React.createRef();
      return gridRefs.current[id];
    };

    const runSearch = useCallback(async () => {
      const params = searchRef.current?.getFormdata?.() ?? {};
      const data = await onSearch(params);
      if (!data) return;
      const next: Record<string, any[]> = {};
      gridIds.forEach((id, i) => {
        next[id] = data[i] || [];
      });
      setDataSources(next);
    }, [gridIds, onSearch]);

    useImperativeHandle(
      ref,
      () => ({
        onSearch: runSearch,
        getParams: () => searchRef.current?.getFormdata?.() ?? {},
        getColumnWidths: () => {
          const out: Record<string, Record<string, number>> = {};
          Object.entries(gridRefs.current).forEach(([gridId, r]) => {
            const cols: any[] = r.current?.getAllGridColumns?.() ?? [];
            const widths: Record<string, number> = {};
            cols.forEach((c) => {
              const id = c?.getColId?.();
              const w = c?.getActualWidth?.();
              if (id && typeof w === "number" && w > 0) widths[id] = Math.round(w);
            });
            out[gridId] = widths;
          });
          return out;
        },
      }),
      [runSearch]
    );

    const render = (node: LayoutNode, key: string): React.ReactNode => {
      switch (node.fg) {
        case "SEARCH":
          /* 조회조건 영역의 행(cd_form = search)만. 폼 행까지 넘기면 SearchPanel 이 그것도 그린다 */
          return (
            <div key={key} className="mb-1">
              <SearchPanel
                ref={searchRef}
                data={formData}
                controls={formControlData.filter((c) => String(c.cd_form || "search") === node.id)}
                {...(onSearchControlsChange
                  ? { designMode: true, onControlsChange: onSearchControlsChange }
                  : {})}
              />
            </div>
          );
        case "GRID":
          return (
            <SnGrid
              key={key}
              ref={gridRef(node.id)}
              columnConfig={grids[node.id] || []}
              rowData={dataSources[node.id] || []}
              etcProps={{
                rowSelection: "single",
                title: node.title || undefined,
                isEditable: editable,
              }}
              events={{}}
            />
          );
        case "TAB":
          return (
            <SnTab
              key={key}
              titles={node.children.map((c) => c.title || c.id)}
              width={"100%"}
              body={node.children.map((c, i) => (
                <div key={`${key}-t${i}`} className="h-full min-h-0">
                  {/* 제목은 탭 이름으로 썼다. 안의 그리드가 또 제목 줄을 그리지 않게 비운다 */}
                  {render({ ...c, title: null }, `${key}-t${i}`)}
                </div>
              ))}
            />
          );
        case "H":
        case "V": {
          const horizontal = node.fg === "H";
          return (
            <div
              key={key}
              className={
                horizontal
                  ? "w-full flex h-full min-h-0 space-x-1.5"
                  : "flex-1 h-full min-h-0 flex flex-col space-y-1.5"
              }
            >
              {node.children.map((c, i) => {
                const childKey = `${key}-${i}`;
                if (c.fg === "SEARCH") return render(c, childKey);
                if (c.size) {
                  return (
                    <SnResizeBar
                      key={childKey}
                      direction={horizontal ? "width" : "height"}
                      id={`${cdMenu}-${c.id}`}
                      orgSize={String(c.size)}
                    >
                      {render(c, childKey)}
                    </SnResizeBar>
                  );
                }
                return (
                  <div key={childKey} className="flex-1 min-h-0 min-w-0">
                    {render(c, childKey)}
                  </div>
                );
              })}
            </div>
          );
        }
        case "FORM": {
          /* 폼 영역 — 그 영역 ID(cd_form)의 sys_formctrl 행으로 SnForm 을 그린다. 저장(2단계)은 아직이라 값만 보여 준다 */
          const controls = formControlData.filter((c) => String(c.cd_form || "") === node.id);
          return (
            <div key={key} className="flex-1 min-h-0 overflow-auto">
              {controls.length > 0 ? (
                <SnForm data={{}} controls={controls} events={{}} />
              ) : (
                <div className="border border-dashed border-gray-300 text-gray-400 p-2">{node.title || node.id}</div>
              )}
            </div>
          );
        }
        default:
          return (
            <div key={key} className="flex-1 min-h-0 border border-dashed border-gray-300 text-gray-400 p-2">
              {node.title || node.id}
            </div>
          );
      }
    };

    if (!tree) {
      return <div className="p-4 text-gray-500">이 메뉴의 화면 정의가 없습니다.</div>;
    }
    return <div className="h-full flex flex-col">{render(tree, "root")}</div>;
  }
);

const SN_COM_LAYOUT = forwardRef(({ Menuprops }: Props, ref) => {
  const cdMenu = Menuprops.cd_menu || "";
  const runtimeRef = useRef<LayoutRuntimeRef>(null);
  const [layout, setLayout] = useState<DefRow[]>([]);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [grids, setGrids] = useState<Record<string, any[]>>({});

  useEffect(() => {
    const fetchform = async () => {
      try {
        const [rows, rtn] = await Promise.all([
          fetchDesignLayout(cdMenu),
          getInitFormData({ cd_menu: cdMenu, cd_module: Menuprops.cd_module }),
        ]);
        setLayout(rows);
        setFormData(rtn?.formData);
        setFormControlData(rtn?.formControlData || []);
        setGrids(rtn?.grid || {});
      } catch (error) {}
    };
    fetchform();
  }, [cdMenu]);

  const onSearch = useCallback(
    async (params: Record<string, any>) => {
      const data: any = await fnGetDataSet({ id: `${cdMenu.toLowerCase()}_01`, params });
      return Array.isArray(data) ? data : undefined;
    },
    [cdMenu]
  );

  useImperativeHandle(ref, () => ({
    onSearch: () => runtimeRef.current?.onSearch(),
  }));

  return (
    <LayoutRuntime
      ref={runtimeRef}
      cdMenu={cdMenu}
      layout={layout}
      formData={formData}
      formControlData={formControlData}
      grids={grids}
      onSearch={onSearch}
    />
  );
});

export default SN_COM_LAYOUT;
